/* eslint-disable max-lines */
// deps
import { useContext, useEffect, useState, useCallback, useRef } from "react";

// internal-deps
import { isAppPlatform, isEmpty, Storage, useMountedRef } from "@swiggy-private/core-ui";
import Blaze from "@swiggy-private/blaze.js";

// api
import { confirmOrder, ConfirmOrderParams } from "@payment-client/apis/Order";

// constants
import { PAYMENT_FALLBACK_STATUS_CODE } from "@payment-client/constants/StatusCodes";
import { OOPS_SOMETHING_WENT_WRONG } from "@payment-client/constants/TextDict";
import { EXPERIMENTS_VALUES } from "@payment-client/constants/Experiments";
import { ORDER_STATUS } from "@payment-client/constants/Order";
import { PLACE_ORDER_DATA, SDK_TURBO_PLACE_ORDER_TIME } from "@payment-client/constants/StorageKeys";

// hooks
import usePolling, { PollingFnData } from "@payment-client/hooks/UsePolling";
import { useLaunchWebviewRefreshOrderIdMismatchAlert } from "./useLaunchAlertPopupForDiffOrder";
import {
    useNavigate,
    CheckoutContext,
    ApiContext,
    CheckoutActionTypes,
    getBridgeSdk,
    recordNREvent,
    NEWRELIC_TABLES,
    BLAZE_EVENTS,
    ConfirmOrderContext,
    trackPSRMetricsForWebviewRefresh,
    PSRMetricSource,
    ConfigContext,
    SwgyHeaderKeysList,
    GlobalHeaderState,
    PublicApiContext,
} from "@swiggy-private/checkout-core";

// helpers
import {
    handleConfirmOrderSuccess,
    getOrderSuccessMessage,
    getConfirmOrderParams,
    isWebPollingEnabled,
} from "@payment-client/helpers/OrderHelper";
import { cleanProcessOrderState, getProcessOrderState } from "@payment-client/helpers/CheckoutStateHelper";
import { showNativeAlert } from "@payment-client/utils/Native";
import { PollingEnabledPaymentFlow } from "@payment-client/includes/PaymentFlow";
import { sendOrderFailedEvent, ORDER_FAIL_INSTANCE } from "@payment-client/helpers/OrderFailedHelper";
import { getPaymentsCheckoutState, getReferrerPaymentsPageRoute } from "@payment-client/helpers/PaymentHelper";
import { isBusinessLineGifting, isIos } from "@payment-client/helpers/Misc";
import {
    getOrderInterruptionAlertData,
    isConfirmApiOrderInterruptionStatusCode,
} from "@payment-client/helpers/OrderInterruptionHelper";
import { getJuspaySdkFeatureAvailability } from "@payment-client/helpers/JuspayHelpers";
import { buildOrderLoadTimePayload } from "@payment-client/helpers/Metrics";

// interfaces
import { ApiResponse } from "@payment-client/interfaces/Api";
import { OrderApiData, OrderData, PostOrderTipDetails } from "@swiggy-private/payment-types";
import { getTurboPaymentMethodFromPlaceOrder } from "@payment-client/helpers/TurboPayment";
import { STORAGE_TYPES } from "@swiggy-private/native-bridge";
import { CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS } from "@swiggy-private/checkout-core";
import { confirmOrderPublic } from "@payment-client/apis/publicOrder";
import { isPublicApiEnabled } from "@swiggy-private/core-utils";

// contexts
import { JuspayContext } from "@payment-client/contexts/JuspayContext";

interface ConfirmOrderProps {
    pollingIntervalInMs: number;
    pollingDurationInMs: number;
}
interface OrderSuccessMessage {
    successMessageType: string;
    successMessage?: string;
    successMessageInfo?: string;
    successMessageV2?: string;
    successMessageInfoV2?: string;
}

type OrderSuccessMessageReturnType = OrderSuccessMessage | null;

// TODO: hack for de tipping. revert after reading success messages from api response
type UseConfirmOrderReturnType = [boolean, OrderSuccessMessageReturnType, boolean, number];

// TODO: hack for de tipping. remove after reading success messages from api response
const isPostOrderTipping = (orderData: OrderData): boolean => {
    const tipDetailList = orderData.tipDetailList ?? [];
    const postOrderTipDetails = tipDetailList.find((tip) => tip.type === "POST_ORDER_TIPPING") as PostOrderTipDetails;

    return postOrderTipDetails?.amount > 0;
};

const useConfirmOrder = (props: ConfirmOrderProps): UseConfirmOrderReturnType => {
    const isMountedRef = useMountedRef();
    const pollingRef = useRef(false);
    const confirmOrderRetryCountRef = useRef(0);
    const apiContextValue = useContext(ApiContext);
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);
    const { setConfirmOrderContext } = useContext(ConfirmOrderContext);
    const [isOrderConfirmed, setOrderConfirmed] = useState(false);
    const [orderSuccessMessage, setOrderSuccessMessage] = useState<OrderSuccessMessageReturnType>(null);
    // TODO: hack for de tipping. remove after reading success messages from api response
    const [isPostOrderTip, setIsPostOrderTip] = useState(false);
    const { launchHalfCardForWebviewRefreshOrderIsMismatch } = useLaunchWebviewRefreshOrderIdMismatchAlert();
    const { enableConfirmOrderDifferentialOrderIdAlert } = useContext(ConfigContext);
    const publicApiConfig = useContext(PublicApiContext);
    const { isSdkAvailable: isJuspaySdkAvailable, featuresAvailability: juspaySdkFeatureAvailable } =
        useContext(JuspayContext);

    const isConfirmOrderEnabled =
        isBusinessLineGifting() ||
        isPublicApiEnabled(
            publicApiConfig || {},
            CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.ORDER_APIS,
            CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.CONFIRM_ORDER,
        );

    const enableConfirmOrderDifferentOrderIdMismatchCheck = useCallback(() => {
        if (typeof window === "undefined") {
            return false;
        }
        const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform);
        const isApp = isAppPlatform(platform);
        return enableConfirmOrderDifferentialOrderIdAlert?.unwrapOr(false) && isApp;
    }, [enableConfirmOrderDifferentialOrderIdAlert]);

    const navigateTo = useNavigate();

    // This is used to store error response of confirm order api while polling
    // It always contains the error response of last api polled
    const confirmOrderApiErr = useRef<ApiResponse>();
    const shouldRetryOnFailure = useCallback(
        (data?: OrderApiData) => confirmOrderRetryCountRef.current < 3 && data != null && data.retryCount > 0,
        [confirmOrderRetryCountRef],
    );

    const handleConfirmOrderDataCallback = useCallback(
        (data: ApiResponse<OrderApiData>) => {
            if (
                checkoutState.type !== CheckoutActionTypes.CONFIRM_ORDER ||
                !isMountedRef.current ||
                isEmpty(checkoutState.payload)
            )
                return;

            const orderData = handleConfirmOrderSuccess(data);
            const successMessage = getOrderSuccessMessage(data);
            if (data) {
                setConfirmOrderContext(data);
            }

            setCheckoutState({
                type: CheckoutActionTypes.CONFIRM_ORDER,
                payload: {
                    orderId: checkoutState.payload.orderId,
                    order: { ...orderData, rawResponse: data.rawResponse?.data },
                    placeOrderData: checkoutState.payload.placeOrderData,
                },
            });

            setOrderConfirmed(true);
            setOrderSuccessMessage(successMessage);
            // TODO: hack for de tipping. remove after reading success messages from api response
            setIsPostOrderTip(isPostOrderTipping(orderData));
        },
        [checkoutState.type, checkoutState.payload, setConfirmOrderContext, isMountedRef, setCheckoutState],
    );

    // confirm order api function to be passed to polling function
    const confirmOrderApi = useCallback(async (): Promise<void> => {
        if (
            !isMountedRef.current ||
            checkoutState.type !== CheckoutActionTypes.CONFIRM_ORDER ||
            isEmpty(checkoutState.payload)
        ) {
            return Promise.resolve();
        }

        try {
            // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
            recordNREvent({
                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                name: "useConfirmOrder",
                orderId: checkoutState.payload.order.orderId || "",
                paymentTxnId: checkoutState.payload.order.paymentTxnId || "",
                method: checkoutState?.payload?.order?.orderPaymentMethod || "",
                type: "regular",
            });
            const confirmOrderParams: ConfirmOrderParams = getConfirmOrderParams(checkoutState.payload.order);

            let currentOrderId = "";
            let appOrderId = "";
            try {
                const orderIdResponse = await getBridgeSdk().storage({
                    type: STORAGE_TYPES.GET,
                    data: {
                        currentOrderId: "",
                    },
                });
                currentOrderId = String(orderIdResponse?.data["currentOrderId"] || "");
                appOrderId = String(checkoutState?.payload?.order?.orderId || "");
                if (enableConfirmOrderDifferentOrderIdMismatchCheck() && currentOrderId !== appOrderId) {
                    launchHalfCardForWebviewRefreshOrderIsMismatch();
                    return;
                }
            } catch (e) {
                try {
                    recordNREvent({
                        eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                        name: "useConfirmOrderAPIOrderIdStoreGetFailed",
                        error: JSON.stringify(e),
                    });
                } catch {
                    //no-op
                }
            }

            const confirmOrderFn = isConfirmOrderEnabled ? confirmOrderPublic : confirmOrder;

            const handleConfirmOrderData = await confirmOrderFn(apiContextValue.host.unwrap(), confirmOrderParams);

            recordNREvent({
                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                name: "useConfirmOrderComparingOrderId",
                currentOrderId: currentOrderId || "",
                appOrderId,
                method: checkoutState?.payload?.order?.orderPaymentMethod || "",
                type: "regular",
            });
            if (currentOrderId !== appOrderId) {
                // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
                recordNREvent({
                    eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                    name: "useConfirmOrderDifferentOrderId",
                    currentOrderId: currentOrderId || "",
                    appOrderId,
                    method: checkoutState?.payload?.order?.orderPaymentMethod || "",
                    type: "regular",
                });
            } else {
                // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
                recordNREvent({
                    eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                    name: "useConfirmOrderSameOrderId",
                    currentOrderId: currentOrderId || "",
                    appOrderId,
                    method: checkoutState?.payload?.order?.orderPaymentMethod || "",
                    type: "regular",
                });
            }

            handleConfirmOrderDataCallback(handleConfirmOrderData);

            // stop polling after confirm order success
            return Promise.resolve();
        } catch (err) {
            // continue polling if we retry count > 0
            if (err != null && shouldRetryOnFailure(err.data)) {
                confirmOrderRetryCountRef.current += 1;
                return Promise.reject();
            }

            // storing err response to handle it after polling is completed
            confirmOrderApiErr.current = err;

            // stop polling on failure
            return Promise.resolve();
        }
    }, [
        isMountedRef,
        checkoutState.type,
        checkoutState.payload,
        apiContextValue.host,
        handleConfirmOrderDataCallback,
        enableConfirmOrderDifferentOrderIdMismatchCheck,
        launchHalfCardForWebviewRefreshOrderIsMismatch,
        shouldRetryOnFailure,
        isConfirmOrderEnabled,
    ]);

    // error handling after polling is completed
    const postPollingHandlerFn = useCallback((): void => {
        if (
            !isMountedRef.current ||
            !confirmOrderApiErr.current ||
            checkoutState.type !== CheckoutActionTypes.CONFIRM_ORDER ||
            isEmpty(checkoutState.payload)
        ) {
            return;
        }
        const { placeOrderData, orderId, order } = checkoutState.payload;

        try {
            trackPSRMetricsForWebviewRefresh(PSRMetricSource.CONFIRM_ORDER_FAILURE, {
                orderId: String(orderId || ""),
                method: String(order?.orderPaymentMethod || ""),
            });
        } catch {
            // no-op
        }

        sendOrderFailedEvent({
            orderId,
            placeOrderData,
            totalBill: String(order.orderTotal),
            failInstance: ORDER_FAIL_INSTANCE.CONFIRM,
            error: confirmOrderApiErr.current,
        });
        const isTurboPaymentMethod = getTurboPaymentMethodFromPlaceOrder(placeOrderData);

        //Checking Juspay SDK availiblity based on feature
        const _isJuspaySdkAvailable = getJuspaySdkFeatureAvailability(
            placeOrderData?.paymentMethodData.groupName,
            isJuspaySdkAvailable,
            juspaySdkFeatureAvailable,
        );

        // record confirm order failed event to NR
        recordNREvent({
            eventType: NEWRELIC_TABLES.PAYMENT_WEB_CONFIRM_ORDER_FAILED,
            method: order.orderPaymentMethod || "",
            orderId,
            retryCount: confirmOrderRetryCountRef.current,
            autopayVariant: confirmOrderApiErr.current?.data?.xpData?.autoPayVariant || "--",
            isTurboPaymentMethod,
            webPollingEnabled: isWebPollingEnabled(order.appRedirectionInfo).toString(),
            isSdkFlow: _isJuspaySdkAvailable.toString(),
        });
        // TODO: remove after IM loader debugging
        Storage.removeItem(PLACE_ORDER_DATA);
        Storage.removeItem(SDK_TURBO_PLACE_ORDER_TIME);

        try {
            const payload = buildOrderLoadTimePayload({
                orderData: order,
                orderId,
                placeOrderData,
                isTurboPaymentMethod,
                _isJuspaySdkAvailable,
            });
            // Record the P2O metrics. From place order to successful confirm order for diffrent payment methods
            Blaze.recordEnd(BLAZE_EVENTS.RECORD_ORDER_LOAD_TIME, {
                ...payload,
                orderStatus: ORDER_STATUS.FAILED,
            });
        } catch {
            // no-op
        }

        const referrerRoute = getReferrerPaymentsPageRoute();

        // For autopay phase 2, if user receives a soft failure asking them to try payment after sometime we show a half card
        if (isConfirmApiOrderInterruptionStatusCode(confirmOrderApiErr?.current)) {
            const orderInterruptionAlertData = getOrderInterruptionAlertData(confirmOrderApiErr?.current);
            const errorCodes = confirmOrderApiErr?.current?.errorCodes;
            setCheckoutState({
                type: CheckoutActionTypes.ORDER_INTERRUPTION,
                payload: {
                    orderInterruptionAlertData,
                    placeOrderData,
                    errorStatusCode: confirmOrderApiErr?.current.statusCode,
                    errorCodes,
                },
            });

            navigateTo(referrerRoute, {}, true);
            return;
            // handling terminal order state (completed/cancelled) via halfcard
        } else if (confirmOrderApiErr.current.statusCode === PAYMENT_FALLBACK_STATUS_CODE) {
            const paymentFailureResponse = confirmOrderApiErr.current.data?.paymentFailureResponse;
            const isFallbackApiEnabled =
                confirmOrderApiErr.current.data?.xpData?.showPaymentFailureReasonHalfCard === "true";

            const autopayPaymentFailure = [
                EXPERIMENTS_VALUES.AUTOPAY_PHASE_2.CONSENT,
                EXPERIMENTS_VALUES.AUTOPAY_PHASE_2.SUBTLE_AUTOPLACE,
            ].includes(confirmOrderApiErr.current?.data?.xpData?.autoPayVariant);

            if (autopayPaymentFailure) {
                setCheckoutState({
                    type: CheckoutActionTypes.AUTOPAY_ELIGIBLE_PAYMENT_FAILED,
                    payload: {
                        paymentFailureResponse,
                        placeOrderData,
                        isFallbackApiEnabled: isFallbackApiEnabled,
                        autoPayPaymentFailureVariant: confirmOrderApiErr.current?.data?.xpData?.autoPayVariant,
                        failedOrder: order,
                    },
                });
            } else {
                // update checkout state to handle payment fallback status
                setCheckoutState({
                    type: CheckoutActionTypes.PAYMENT_FAILED,
                    payload: {
                        paymentFailureResponse,
                        placeOrderData,
                        isFallbackApiEnabled,
                        failedOrder: order,
                    },
                });

                navigateTo(referrerRoute, {}, true);
            }

            return;
        }

        // clear process order state and set state to show payments screen again
        // To handle scenario when checkout return statusCode 2 | 1 (No order in session)
        cleanProcessOrderState();
        try {
            // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
            const processOrderState = getProcessOrderState();
            recordNREvent({
                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                name: "useConfirmOrderCleanState",
                isEmptyProcessOrderState: String(isEmpty(processOrderState)),
                orderId: processOrderState?.orderId || "--",
            });
        } catch {
            /* No-op*/
        }
        // Handled Swiggy Money & Regular Payments Checkout State
        const paymentsState = getPaymentsCheckoutState();
        setCheckoutState({ type: paymentsState });
        const errorMessage = confirmOrderApiErr.current.statusMessage || OOPS_SOMETHING_WENT_WRONG;
        showNativeAlert({
            message: `${errorMessage} (Err Code: ${confirmOrderApiErr.current.statusCode})`,
        });

        navigateTo(referrerRoute, {}, true);
    }, [checkoutState.payload, checkoutState.type, isMountedRef, navigateTo, setCheckoutState]);

    const pollingFn = usePolling(postPollingHandlerFn);
    const invokePollFn = useCallback(
        (pollingData: PollingFnData) => {
            pollingFn(pollingData);
        },
        [pollingFn],
    );

    useEffect(() => {
        // is mounted check..
        if (
            pollingRef.current ||
            !isMountedRef.current ||
            checkoutState.type !== CheckoutActionTypes.CONFIRM_ORDER ||
            isEmpty(checkoutState.payload)
        ) {
            return;
        }

        // Set App Idle state to default on coming to confirm order
        if (isIos()) {
            getBridgeSdk()
                .setAppStateInIdleMode({ active: false })
                .catch(() => void 0);
        }

        // Polling is not required if post order status is processing (COD, WALLET, LAZYPAY)
        if (checkoutState.payload.isOrderProcessed) {
            setOrderConfirmed(true);
            // TODO: hack for de tipping. remove after reading success messages from api response
            setIsPostOrderTip(isPostOrderTipping(checkoutState.payload.order));
            return;
        }

        pollingRef.current = true;

        let _promise = Promise.resolve(false);
        const _paymentFlowInstance = PollingEnabledPaymentFlow.getInstance();
        const _orderId = checkoutState.payload.orderId;

        /**
            Bug Fix: For edit order like flows, eg., de tip, auto pay etc
            order id and txn id are not same. we should compare both
            this would break flows which uses `usePollingTxnStatus` as they also make
            confirm order call, flows like UPI Collect.
        */
        if (
            _paymentFlowInstance &&
            (_paymentFlowInstance.getOrderId() == _orderId || _paymentFlowInstance.getPaymentTxnId() == _orderId)
        ) {
            const _confirmOrderPromise = _paymentFlowInstance.getConfirmOrderPromise();

            if (_confirmOrderPromise == null) {
                _paymentFlowInstance.stop();
            } else {
                // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
                recordNREvent({
                    eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                    name: "useConfirmOrder",
                    orderId: checkoutState?.payload?.order?.orderId || "",
                    paymentFlowOrderId: _paymentFlowInstance.getOrderId(),
                    paymentFlowTxnId: _paymentFlowInstance.getPaymentTxnId(),
                    paymentTxnId: checkoutState?.payload?.order?.paymentTxnId || "",
                    method: checkoutState?.payload?.order?.orderPaymentMethod || "",
                    type: "polling",
                });

                getBridgeSdk()
                    .storage({
                        type: STORAGE_TYPES.GET,
                        data: {
                            currentOrderId: "",
                        },
                    })
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .then((orderIdResponse: any) => {
                        const currentOrderId = orderIdResponse?.data?.["currentOrderId"] || "--";
                        const appOrderId = checkoutState?.payload?.order?.orderId || "";
                        if (enableConfirmOrderDifferentOrderIdMismatchCheck() && currentOrderId !== appOrderId) {
                            launchHalfCardForWebviewRefreshOrderIsMismatch();
                            return true;
                        }
                        _promise = _confirmOrderPromise
                            .then((response) => {
                                try {
                                    handleConfirmOrderDataCallback(response);

                                    getBridgeSdk()
                                        .storage({
                                            type: STORAGE_TYPES.GET,
                                            data: {
                                                currentOrderId: "",
                                            },
                                        })
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        .then((orderIdResponse: any) => {
                                            const currentOrderId = orderIdResponse?.data?.["currentOrderId"] || "--";
                                            const appOrderId = checkoutState?.payload?.order?.orderId || "";
                                            recordNREvent({
                                                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                                                name: "useConfirmOrderComparingOrderId",
                                                currentOrderId: currentOrderId || "",
                                                appOrderId,
                                                type: "polling",
                                                method: checkoutState?.payload?.order?.orderPaymentMethod || "",
                                            });
                                            if (currentOrderId !== appOrderId) {
                                                // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
                                                recordNREvent({
                                                    eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                                                    name: "useConfirmOrderDifferentOrderId",
                                                    currentOrderId: currentOrderId || "",
                                                    appOrderId,
                                                    type: "polling",
                                                    method: checkoutState?.payload?.order?.orderPaymentMethod || "",
                                                });
                                            } else {
                                                // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
                                                recordNREvent({
                                                    eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                                                    name: "useConfirmOrderSameOrderId",
                                                    currentOrderId: currentOrderId || "",
                                                    appOrderId,
                                                    type: "polling",
                                                    method: checkoutState?.payload?.order?.orderPaymentMethod || "",
                                                });
                                            }
                                        })
                                        .catch(() => void 0);

                                    return true;
                                } catch (err) {
                                    return false;
                                }
                            })
                            .catch((_) => false)
                            .then((r) => {
                                _paymentFlowInstance?.stop();
                                return r;
                            });
                    })
                    .catch((e) => {
                        try {
                            recordNREvent({
                                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                                name: "useConfirmOrderPollOrderIdStoreGetFailed",
                                error: JSON.stringify(e),
                            });
                        } catch {
                            //no-op
                        }
                        return false;
                    });
            }
        }

        // Initiate poll for confirm order
        _promise.then((result) => {
            if (result || isOrderConfirmed || !isMountedRef.current) {
                return;
            }

            invokePollFn({
                pollingDurationInMs: props.pollingDurationInMs,
                pollingIntervalInMs: props.pollingIntervalInMs,
                apiFn: confirmOrderApi,
            });
        });
    }, [
        pollingRef,
        checkoutState,
        isMountedRef,
        setOrderConfirmed,
        isOrderConfirmed,
        props.pollingDurationInMs,
        props.pollingIntervalInMs,
        confirmOrderApi,
        invokePollFn,
        handleConfirmOrderDataCallback,
        launchHalfCardForWebviewRefreshOrderIsMismatch,
        enableConfirmOrderDifferentOrderIdMismatchCheck,
    ]);

    return [isOrderConfirmed, orderSuccessMessage, isPostOrderTip, confirmOrderRetryCountRef.current];
};

export default useConfirmOrder;
